import { parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { SiteModelCoordinate } from './geckoSiteModel'
import { RolodexEntryJson, RolodexEntryType } from './rolodexEntryFields'

export enum TicketStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN PROGRESS',
  DONE = 'DONE',
  NEW = 'NEW',
  ACKED = 'ACKED',
}

export enum ObservationType {
  THERMAL = 'Thermal',
  VISUAL = 'Visual',
  ACOUSTIC = 'Acoustic',
  VIBRATIONAL = 'Vibrational',
  OIL = 'Oil',
  INFRARED = 'IR',
}

export enum Department {
  PDM = 'PdM',
  OPERATIONS = 'Operations',
  ASSET_MANAGEMENT = 'Asset Management',
}

export enum Severity {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical',
}

/**
 * An entry that holds an Observation taken from site - right now uesed for adding indications to point clouds
 */
export class GeckoSiteObservation extends GeckoEntryBase<
  {
    name?: string
    unit_id?: string
    area_id?: string
    alive?: boolean
    [key: string]: unknown
    kind?: string
    department?: Department
    severity?: Severity
    type?: ObservationType
    maxarCoordinates?: {
      coordinate: SiteModelCoordinate
      rotation: [number, number, number]
    }
    pointCloudCoordinates?: {
      coordinate: [number, number, number]
    }
    description?: string
    images?: string[]
    lastUpdated?: number

    status?: TicketStatus
    component?: string
    condition?: string
    equipment?: string
    wo_number?: string
    date_found?: number | string
    notification_number?: string
    original_created_date?: number | string
  },
  {
    site: string
    organization: string
    [RolodexEntryType.POTREE_POINT_CLOUD]?: string
    ticket?: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        name: parseString(json.data.name) ?? '',
        unit_id: parseString(json.data.unit_id),
        area_id: parseString(json.data.area_id),
        alive: parseString(json.data.alive) === 'true',
        kind: parseString(json.data.kind),
        department: parseString(json.data.department) as Department | undefined,
        severity: parseString(json.data.severity) as Severity | undefined,
        type: parseString(json.data.type) as ObservationType | undefined,
        description: parseString(json.data.description),
        images: Array.isArray(json.data.images)
          ? (json.data.images as string[])
          : undefined,
        maxarCoordinates: json.data.maxarCoordinates as
          | {
              coordinate: SiteModelCoordinate
              rotation: [number, number, number]
            }
          | undefined,
        pointCloudCoordinates: json.data.pointCloudCoordinates as
          | {
              coordinate: [number, number, number]
            }
          | undefined,
        status: parseString(json.data.status) as TicketStatus | undefined,
        component: parseString(json.data.component),
        condition: parseString(json.data.condition),
        equipment: parseString(json.data.equipment),
        wo_number: parseString(json.data.wo_number),
        date_found:
          typeof json.data.date_found === 'string' ||
          typeof json.data.date_found === 'number'
            ? json.data.date_found
            : undefined,
        notification_number: parseString(json.data.notification_number),
        original_created_date:
          typeof json.data.original_created_date === 'string' ||
          typeof json.data.original_created_date === 'number'
            ? json.data.original_created_date
            : undefined,
        lastUpdated: parseNumber(json.data.lastUpdated),
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        [RolodexEntryType.POTREE_POINT_CLOUD]: parseString(
          json.tags[RolodexEntryType.POTREE_POINT_CLOUD]
        ),
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a point cloud from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoSiteObservation(json)
  }
}
